import { useQueryClient } from '@tanstack/react-query';
import useAppMutation from 'hooks/useAppMutation';
import useNotify from 'hooks/useNotify';
import { PROPERTY_OWNERS_BASE_QUERY_KEY } from 'pages/property/owners/usePropertyOwners';
import { useTranslation } from 'react-i18next';
import API from 'services/API';
import { getErrorMessage } from 'utils/axiosExceptionUtils';

interface OwnerNotesUpdateBulkRequest {
  updateContext: {
    applyToAll: boolean;
    propertyUids: string[];
  };
  ownerNotes: {
    notes: string;
  };
  collectAndRemitTaxes: boolean;
  copyOwnerWhenBookingConfirmed: boolean;
}

const useOwnerNotesBulkMutation = () => {
  const { t } = useTranslation();
  const { notifyError } = useNotify();
  const queryClient = useQueryClient();

  const { mutateAsync: updateOwnerNotes, isLoading: isUpdating } =
    useAppMutation(
      async (payload: OwnerNotesUpdateBulkRequest) => {
        const response = await API.post(
          '/api/internal/property-ownership/notes/bulk-update',
          payload,
        );
        return response.data;
      },
      {
        onSuccess: () => {
          // Reset the query to re-render communication settings form
          queryClient.resetQueries({
            queryKey: [PROPERTY_OWNERS_BASE_QUERY_KEY],
          });
        },
        onError: (error: any) => {
          notifyError(
            getErrorMessage({
              apiErrorCode: error.response.data?.apiErrorCode,
              apiErrorMessage: error.response.data?.apiErrorMessage,
              baseKey: 'pageProperty.apiErrorCodes.',
              t,
            }),
          );
        },
      },
    );

  return { isUpdating, updateOwnerNotes };
};

export default useOwnerNotesBulkMutation;
